import { useLayoutEffect, useRef, useState } from "react";

/**
 * Measures an element's height on mount and on every resize via ResizeObserver.
 * Returns a [ref, height] tuple — attach the ref to the element to measure.
 */
export const useMeasuredHeight = <T extends HTMLElement>(): [
  React.RefObject<T | null>,
  number,
] => {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    setHeight(element.scrollHeight);

    const observer = new ResizeObserver((entries) => {
      // Use scrollHeight (instead of contentRect.height) to stay consistent
      // with the initial measurement and to include padding.
      const target = entries[0]?.target as HTMLElement | undefined;
      if (target) setHeight(target.scrollHeight);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, height];
};
